<template>
  <div class="i-explorable" :class="computedClass">
    <template v-if="showSidebar">
      <aside class="i-explorable__sidebar">
        <slot name="sidebar"></slot>
      </aside>
    </template>
    <template v-if="showZoomButtons">
      <aside class="zoom-buttons">
        <div class="zoom-buttons__group">
          <button class="zoom-in" @click="zoomInOut(+1, buttonZoomStep)">
            <span>&plus;</span>
          </button>
          <hr />
          <button class="zoom-out" @click="zoomInOut(-1, buttonZoomStep)">
            <span>&minus;</span>
          </button>
        </div>
        <div class="zoom-buttons__group">
          <button class="zoom-reset" @click="reset()">
            <img :src="targetIcon" />
          </button>
        </div>
        <br />
        <div class="zoom-buttons__group" v-if="floor === '5NP' || floor === '6NP'">
          <button class="zoom-in" @click="openFloor('6NP')">
            <span>↑</span>
          </button>
          <hr />
          <button class="zoom-out" @click="openFloor('5NP')">
            <span>↓</span>
          </button>
        </div>
        <div class="zoom-buttons__group" v-if="floor === 'veza-5NP' || floor === 'veza-6NP'">
          <button class="zoom-in" @click="openFloor('veza-6NP')">
            <span>↑</span>
          </button>
          <hr />
          <button class="zoom-out" @click="openFloor('veza-5NP')">
            <span>↓</span>
          </button>
        </div>
      </aside>
    </template>

    <!--Hlavna budova-->
    <aside>
      <div style="position: relative" v-if="floor === '5NP' || floor === '6NP'">
        <h4 style="font-size: 20px; position: absolute; left: 100px; top: -10px;">
          {{lang === 'sk' ? 'Výber poschodia' : 'Floor selection'}}
        </h4>
        <svg
          width="273"
          height="147"
          viewBox="-100 0 300 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          @click="switchFloorsMain"
          style="position: absolute; z-index: 1;"
        >
          <path
            style="cursor: pointer"
            :fill="floor === '5NP' ? path1Color : path2Color"
            fill-opacity="0.7"
            d="M68.5 31L4 95.5H108.5L173 31H68.5Z"
          />
          <path
            style="cursor: pointer"
            :fill="floor === '5NP' ? path2Color : path1Color"
            fill-opacity="0.7"
            d="M69 3.5L4.5 68H109L173.5 3.5H69Z"
          />
        </svg>
      </div>
    </aside>

     <!--Veza-->
     <aside>
      <div style="position: relative" v-if="floor === 'veza-5NP' || floor === 'veza-6NP'">
        <h4 style="font-size: 20px; position: absolute; left: 100px; top: -10px;">
          {{lang === 'sk' ? 'Výber poschodia' : 'Floor selection'}} 
        </h4>
        <svg
          width="273"
          height="147"
          viewBox="-100 0 300 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          @click="switchFloors"
          style="position: absolute; z-index: 1;"
        >
          <path
            style="cursor: pointer"
            :fill="floor === 'veza-5NP' ? path1Color : path2Color"
            fill-opacity="0.7"
            d="M68.5 31L4 95.5H108.5L173 31H68.5Z"
          />
          <path
            style="cursor: pointer"
            :fill="floor === 'veza-5NP' ? path2Color : path1Color"
            fill-opacity="0.7"
            d="M69 3.5L4.5 68H109L173.5 3.5H69Z"
          />
        </svg>
      </div>
    </aside>

    <main class="i-explorable__body" :style="computedStyle" ref="el">
      <slot></slot>
    </main>
  </div>
</template>

<script>
import { XY } from 'utils/misc';
import { Tweak, wait } from 'utils/timing';
import { mapGetters } from 'utils/misc';
import { IMG_URL } from 'src/constants';
import targetIcon from 'src/assets/img/target-icon.png';

const height = (el) => el.getBoundingClientRect().height;
const width = (el) => el.getBoundingClientRect().width;

export default {
  name: 'i-explorable',
  props: {
    // Position
    initialX: { type: Number, default: 0 },
    initialY: { type: Number, default: 0 },
    // Zoom
    initialZoom: { type: Number, default: 1 },
    minZoom: { type: Number, default: 1 },
    maxZoom: { type: Number, default: 3 },
    wheelZoomStep: { type: Number, default: 0.1 },
    buttonZoomStep: { type: Number, default: 0.2 },
    zoomAnimationDuration: { type: Number, default: 0 },
    // Settings
    enableGrab: { type: Boolean, default: true },
    enableStrictBoundaries: { type: Boolean, default: true },
    enableZoom: { type: Boolean, default: true },
    enableMouseWheelZoom: { type: Boolean, default: true },
    // Components
    showSidebar: { type: Boolean, default: false },
    showZoomButtons: { type: Boolean, default: true }
  },
  data() {
    return {
      IMG_URL,
      // Transform data
      zoom: { scale: 1 },
      xy: new XY(0, 0),
      // Watchers
      watcher: null,
      boundaryWatcher: null,
      // UI state
      grabbing: false,
      hover: false,
      animating: false,

      path1Color: '#0E503C',
      path2Color: '#D9D9D9',
      targetIcon
    };
  },
  computed: {
    computedStyle() {
      return {
        transformOrigin: 'center center',
        transform: `translateX(${this.xy.x}px) translateY(${this.xy.y}px) scale(${this.zoom.scale})`
      };
    },
    computedClass() {
      return [
        // Settings
        this.enableGrab ? 'enable-grab' : null,
        this.enableStrictBoundaries ? 'enable-strict-boundaries' : null,
        this.enableZoom ? 'enable-zoom' : null,
        this.enableMouseWheelZoom ? 'enable-mouse-wheel-zoom' : null,
        // Components
        this.showZoomButtons ? 'show-zoom-buttons' : null,
        this.showSidebar ? 'show-sidebar' : null,
        // UI state
        this.grabbing ? 'grabbing' : null,
        this.hover ? 'hover' : null,
        this.animating ? 'animating' : null
      ];
    },
    ...mapGetters({
      slide: ['getSlide', 'setSlide'],
      floor: ['getFloor', 'setFloor'],
      lang: ['getLang','setLang']
    })
  },
  methods: {
    openFloor(floor) {
      this.slide = 2;
      this.floor = floor;
    },

    switchFloorsMain() {
      if (this.floor === '6NP') {
        this.floor = '5NP';
        this.path1Color = '#0E503C';
        this.path2Color = '#D9D9D9';
      } else {
        this.floor = '6NP';
        this.path1Color = '#D9D9D9';
        this.path2Color = '#0E503C';
      }
    },
    switchFloors() {
      if (this.floor === 'veza-6NP') {
        this.floor = 'veza-5NP';
        this.path1Color = '#0E503C';
        this.path2Color = '#D9D9D9';
      } else {
        this.floor = 'veza-6NP';
        this.path1Color = '#D9D9D9';
        this.path2Color = '#0E503C';
      }
    },

    checkBoundary() {
      if (this.enableStrictBoundaries && this.$refs.hasOwnProperty('el')) {
        const el = this.$refs.el;
        const parent = el.parentNode;

        const deltaWidth = width(el) - width(parent);
        const deltaHeight = height(el) - height(parent);

        const rangeX = Math.max(0, deltaWidth) / 2;
        const rangeY = Math.max(0, deltaHeight) / 2;

        this.xy.x = Math.max(-rangeX, Math.min(this.xy.x, rangeX));
        this.xy.y = Math.max(-rangeY, Math.min(this.xy.y, rangeY));

        // Save history
        // this.storageManager.setItem("i-explorable.xy", this.xy);
        // this.storageManager.setItem("i-explorable.zoom", this.zoom.scale);
      }
    },
    moveBy(xy) {
      this.xy.add(xy);
      this.checkBoundary();
    },
    moveTo(xy) {
      this.xy.x = xy.x;
      this.xy.y = xy.y;
      this.checkBoundary();
    },
    reset() {
      this.moveTo(new XY(this.initialX, this.initialY));

      this.zoom.scale = Math.max(this.minZoom, Math.min(this.maxZoom, this.initialZoom));

      wait(0).then(() => {
        this.checkBoundary();
        this.$emit('zoom', { zoom: this.zoom.scale });
      });
    },
    zoomInOut(delta, step) {
      if (this.enableZoom && !this.animating) {
        const zoomBefore = this.zoom.scale;

        const zoomAfter = Math.max(
          this.minZoom,
          Math.min(this.maxZoom, this.zoom.scale + step * delta)
        );

        if (zoomBefore != zoomAfter) {
          this.animating = true;

          new Tweak()
            .duration(this.zoomAnimationDuration)
            .from(this.zoom)
            .to({ scale: zoomAfter })
            .onEnd(() => {
              this.$emit('zoom', { zoom: zoomAfter });
              this.checkBoundary();
              this.animating = false;
            })
            .start();
        }
      }
    }
  },
  watch: {
    'mouseTracker.lastMove'(newValue) {
      if (this.enableGrab && this.watcher && this.watcher.grab) {
        this.moveBy(this.mouseTracker.delta);
      }
    },
    'mouseTracker.wheels'(newValue, oldValue) {
      if (this.enableMouseWheelZoom && this.watcher.hover) {
        this.zoomInOut(oldValue - newValue, this.wheelZoomStep);
      }
    },
    'watcher.grab'(newValue) {
      if (this.enableGrab) {
        this.grabbing = newValue;

        if (newValue) {
          this.$emit('grabstart');
        } else {
          this.$emit('grabend', { history: this.watcher.grabHistory });
        }
      }
    },
    'watcher.hover'(newValue) {
      this.hover = newValue;
      this.$emit(newValue ? 'hoverstart' : 'hoverend');
    }
  },
  created() {
    this.reset();
  },
  mounted() {
    this.watcher = this.mouseTracker.watch(this.$refs.el);
    this.boundaryWatcher = this.mouseTracker.watch(this.$refs.el.parentNode);

    // Get items from history
    // this.zoom.scale = this.storageManager.getItem("i-explorable.zoom");
    // wait(0).then(() => this.$emit("zoom", { zoom: this.zoom.scale }));

    // this.xy.x = this.storageManager.getItem("i-explorable.xy.x");
    // this.xy.y = this.storageManager.getItem("i-explorable.xy.y");
  },
  beforeDestroy() {
    this.mouseTracker.stopWatching(this.$refs.el);
    this.mouseTracker.stopWatching(this.$refs.el.parentNode);
  }
};
</script>

<style>
.i-explorable {
  position: relative;

  width: 100%;
  height: 100%;
}

.show-sidebar .i-explorable__body {
  margin-right: 75px; /* // todo: move this to property */
}

/* Body */
.i-explorable__body {
  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
}

.enable-grab .i-explorable__body {
  cursor: grab;
}

.enable-grab.grabbing .i-explorable__body {
  cursor: grabbing;
}

/* Sidebar */
.i-explorable__sidebar {
  position: absolute;
  z-index: 1000;
  right: 0;
  top: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 75px; /* // todo: move this to property */
  height: 100%;

  padding: 10px 0;
}

/* Zoom buttons */
.zoom-buttons {
  position: absolute;
  z-index: 1000;
  top: 20px;
  left: 20px;

  background-color: transparent;
}

.zoom-buttons__group {
  border-radius: 8px;

  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.3);

  background-color: white;
}

.zoom-buttons button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 29px;
  height: 29px;

  padding: 0;
  margin: 0;
  outline: 0;
  border: 0;
  border-radius: 8px;

  text-align: center;
  font-weight: 700;
  font-size: 22px;

  color: #666;
  background: #fff;

  transition: color 300ms ease-in-out;

  cursor: pointer;
}

.zoom-buttons button:hover {
  color: #333;
}

.zoom-buttons .zoom-reset {
  margin-top: 10px;
}

.zoom-buttons .zoom-reset img {
  width: 65%;
  height: 65%;
  opacity: 0.7;
}

.zoom-buttons__group hr {
  margin: 0 3px;
  height: 1px;

  border: 0;

  background-color: #eee;
}
</style>
